import { FC, Fragment, useState } from "react";
import { HowToVote } from "@mui/icons-material";

import { useMetamask, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, type IPonziRule, TokenType } from "@framework/types";
import ERC20SimpleApproveABI from "@framework/abis/json/ERC20Simple/approve.json";

import { AllowanceDialog, IAllowanceDto } from "./dialog";

export interface IPonziAllowanceButtonProps {
  className?: string;
  disabled?: boolean;
  rule: IPonziRule;
  variant?: ListActionVariant;
}

export const PonziAllowanceButton: FC<IPonziAllowanceButtonProps> = props => {
  const { className, disabled, rule, variant } = props;

  const [isAllowanceDialogOpen, setIsAllowanceDialogOpen] = useState(false);

  const { writeContractAsync } = useWriteContractWithSimulation();

  const handleAllowance = (): void => {
    setIsAllowanceDialogOpen(true);
  };

  const handleAllowanceCancel = (): void => {
    setIsAllowanceDialogOpen(false);
  };

  const { execute: metaFn } = useMetamask(async (values: IAllowanceDto) => {
    if (rule.deposit?.components[0].tokenType === TokenType.ERC20) {
      return writeContractAsync({
        address: rule.deposit?.components[0].contract!.address as AddressType,
        abi: ERC20SimpleApproveABI,
        functionName: "approve",
        args: [rule.contract.address, values.amount],
      });
    } else {
      throw new Error("unsupported token type");
    }
  });

  const handleAllowanceConfirm = async (values: IAllowanceDto): Promise<void> => {
    await metaFn(values).finally(() => {
      setIsAllowanceDialogOpen(false);
    });
  };

  const isDisabled = rule.deposit?.components.every(component => component.contract!.contractType === TokenType.NATIVE);

  return (
    <Fragment>
      <ListAction
        onClick={handleAllowance}
        icon={HowToVote}
        message="form.tips.allowance"
        className={className}
        dataTestId="PonziAllowanceButton"
        disabled={isDisabled || disabled}
        variant={variant}
      />
      <AllowanceDialog
        onCancel={handleAllowanceCancel}
        onConfirm={handleAllowanceConfirm}
        open={isAllowanceDialogOpen}
        initialValues={{
          amount: rule.deposit?.components[0].amount || "0",
          decimals: rule.deposit?.components[0].contract!.decimals || 18,
        }}
      />
    </Fragment>
  );
};
